import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Users, Mail, Bell, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const AUDIENCES = [
  { key: "all_beta", label: "All Beta Vendors", color: "#F4A83B" },
  { key: "cake_pop_beta", label: "Cake Pop Beta Vendors", color: "#FF7E67" },
  { key: "farm_stand_beta", label: "Farm Stand Beta Vendors", color: "#8EAC50" },
];

export default function AdminBroadcastTab() {
  const [audience, setAudience] = useState("all_beta");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);

  const loadLog = async () => {
    setLoadingLog(true);
    try {
      const { data } = await api.get("/admin/broadcasts");
      setLog(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load broadcast log");
    } finally {
      setLoadingLog(false);
    }
  };
  useEffect(() => { loadLog(); }, []);

  const audienceMeta = useMemo(() => AUDIENCES.find((a) => a.key === audience) || AUDIENCES[0], [audience]);

  const send = async () => {
    if (!subject.trim()) { toast.error("Add a subject"); return; }
    if (!message.trim()) { toast.error("Add a message"); return; }
    if (!window.confirm(`Send this broadcast to ${audienceMeta.label}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcasts", { audience, subject: subject.trim(), message: message.trim() });
      toast.success(`Broadcast sent to ${data.recipient_count} recipients`);
      setSubject("");
      setMessage("");
      // Give the background dispatcher a beat to finish so counters show up nicely.
      setTimeout(loadLog, 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-broadcast-panel">
      {/* Composer */}
      <div className="cp-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg text-[#3B2C24] flex items-center gap-2">
            <Send className="w-4 h-4 text-[#FF7E67]" /> New broadcast
          </h3>
          <span className="text-[11px] uppercase tracking-widest text-[#857163]">Beta program only</span>
        </div>

        <div>
          <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide mb-2 block">Audience</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {AUDIENCES.map((a) => {
              const active = a.key === audience;
              return (
                <button
                  key={a.key}
                  onClick={() => setAudience(a.key)}
                  className={`px-3 py-2.5 rounded-2xl text-sm font-semibold text-left transition border-2 ${active ? "text-white" : "bg-white text-[#3B2C24] border-[#EBDCD1] hover:border-[#FF7E67]/40"}`}
                  style={active ? { background: a.color, borderColor: a.color } : {}}
                  data-testid={`broadcast-audience-${a.key}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    <span>{a.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide mb-2 block">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="Short and clear — shows as email subject and notification headline"
            className="cp-input w-full"
            data-testid="broadcast-subject"
          />
          <div className="text-[10px] text-[#857163] mt-1 text-right">{subject.length}/200</div>
        </div>

        <div>
          <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide mb-2 block">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={7}
            placeholder="Write your update to beta vendors…"
            className="cp-input w-full resize-y"
            data-testid="broadcast-message"
          />
          <div className="text-[10px] text-[#857163] mt-1 text-right">{message.length}/5000</div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <div className="text-xs text-[#857163] flex items-center gap-3">
            <span className="flex items-center gap-1"><Bell className="w-3 h-3" /> In-app</span>
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email (Resend)</span>
          </div>
          <button
            onClick={send}
            disabled={sending || !subject.trim() || !message.trim()}
            className="px-5 py-2.5 rounded-2xl bg-[#FF7E67] text-white font-semibold text-sm hover:opacity-90 transition disabled:opacity-60 flex items-center gap-2"
            data-testid="broadcast-send"
          >
            <Send className="w-4 h-4" /> {sending ? "Sending…" : `Send to ${audienceMeta.label}`}
          </button>
        </div>
      </div>

      {/* Log */}
      <div className="cp-card overflow-hidden" data-testid="broadcast-log">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#EBDCD1]">
          <h3 className="font-heading text-lg text-[#3B2C24]">Sent broadcasts</h3>
          <button
            onClick={loadLog}
            className="text-xs text-[#857163] hover:text-[#3B2C24] flex items-center gap-1"
            data-testid="broadcast-log-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingLog ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {loadingLog ? (
          <div className="p-8 text-center text-[#857163] text-sm">Loading…</div>
        ) : log.length === 0 ? (
          <div className="p-8 text-center text-[#857163] text-sm">No broadcasts sent yet.</div>
        ) : (
          <div className="divide-y divide-[#EBDCD1]/50">
            {log.map((b) => {
              const meta = AUDIENCES.find((a) => a.key === b.audience_key) || { color: "#857163", label: b.audience_label };
              let rel = "";
              try { rel = formatDistanceToNow(new Date(b.created_at), { addSuffix: true }); } catch { rel = ""; }
              const dateFmt = (() => { try { return new Date(b.created_at).toLocaleString(); } catch { return b.created_at; } })();
              return (
                <div key={b.id} className="px-5 py-4" data-testid={`broadcast-row-${b.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-white px-2 py-0.5 rounded-full" style={{ background: meta.color }}>
                        <Users className="w-3 h-3" /> {b.audience_label}
                      </span>
                      <span className="text-xs text-[#857163]">{b.recipient_count} recipient{b.recipient_count === 1 ? "" : "s"}</span>
                      <span className="text-xs text-[#857163]">·</span>
                      <span className="text-xs text-[#857163] flex items-center gap-1"><Mail className="w-3 h-3" /> {b.delivered_email_count} sent{b.failed_email_count > 0 ? ` · ${b.failed_email_count} failed` : ""}</span>
                    </div>
                    <div className="text-xs text-[#857163]" title={dateFmt}>{rel || dateFmt}</div>
                  </div>
                  <div className="mt-2 font-semibold text-[#3B2C24]" data-testid={`broadcast-subject-${b.id}`}>{b.subject}</div>
                  <div className="mt-1 text-sm text-[#3B2C24] whitespace-pre-wrap" data-testid={`broadcast-message-${b.id}`}>{b.message}</div>
                  {b.sent_by_username && (
                    <div className="mt-2 text-[11px] text-[#857163]">Sent by @{b.sent_by_username}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
